import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Modal,
  Skeleton,
  CircularProgress,
  Fade,
  Backdrop,
  Dialog,
  DialogContent,
  TextField,
} from '@mui/material';
import { useState, useEffect } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import styles, { COMMON_MUI_STYLES } from '../../styles';
import SingleChart from '../../components/SingleChart';
import MultiLineChart from '../../components/MultiChart';
import SkinHealthMetrics from '../../components/UserScores';
import AnalysisHistory from '../../components/AnalysisHistory';
import Layout from '../../components/Layout';
import Recommendation from '../../components/Recommendation';
import { getConcernTranslation } from '../../services/concern-mappings';
import { getDistributorInfoAndName } from '../../utils/tokenUtils';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { ConcernDescriptions } from './concerns-description';
import ApiService from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import type { SkinConcern } from './types';
import type { UserSkinProfile } from '../../types';
import CustomerDataTable from './CustomerDataTable';
import DistributorCustomerDataTable from './DistributorCustomerDataTable';
import DistributorDistributorDataTable from './DistributorDistributorDataTable';

/** Set to true to show Single Chart and Multi Chart on profile details. */
const SHOW_SKIN_CHARTS = false;

const getBoxStyles = (color: number) => {
  switch (color) {
    case 1:
      return {
        borderRadius: '500px',
        border: { xs: "2px solid #51A351", md: '3px solid #51A351' },
        backgroundColor: '#7D7',
      };
    case 2:
      return {
        borderRadius: '500px',
        border: { xs: "2px solid #C6872F", md: '3px solid #C6872F' },
        backgroundColor: '#FFB347',
      };
    case 3:
      return {
        borderRadius: '500px',
        border: { xs: "2px solid #C53E37", md: '3px solid #C53E37' },
        backgroundColor: '#FF6961',
      };
    default:
      return {};
  }
};

export type MoreUserDetailsViewProps = {
  role: 1 | 2 | null;
  profileId: string;
  isRTL: boolean;
  getTranslation: (key: string, defaultValue?: string) => string;
  userProfilesCache: any;
  skinOutput: SkinConcern[];
  score: number;
  selectedAnnotationImage: string | null;
  selectedConcernId: string | null;
  imageLoading: boolean;
  isImageLoaded: boolean;
  fullImageModalOpen: boolean;
  moreProfileData: UserSkinProfile[];
  userProfileData: { first_name?: string; last_name?: string; phone_number?: string };
  filteredProfileData: UserSkinProfile[];
  selectedProfile?: UserSkinProfile;
  isLoadingDetails: boolean;
  setFullImageModalOpen: (open: boolean) => void;
  handleCopyEmail: () => Promise<void>;
  handleConcernClick: (issue: SkinConcern) => void;
  handleCloseAnnotationModal: () => void;
  handleNavigateBack: () => void;
};

export const MoreUserDetailsView = (props: MoreUserDetailsViewProps) => {
  const { setSnackbar, token, distributorId, sessionId, distributorTableView } = useAppContext();

  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isUserDeactivated, setIsUserDeactivated] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [isProfileImageLoaded, setIsProfileImageLoaded] = useState(false);

  const {
    role,
    profileId,
    isRTL,
    getTranslation,
    userProfilesCache,
    skinOutput,
    score,
    selectedAnnotationImage,
    selectedConcernId,
    isImageLoaded,
    fullImageModalOpen,
    moreProfileData,
    userProfileData,
    filteredProfileData,
    selectedProfile,
    isLoadingDetails,
    setFullImageModalOpen,
    handleCopyEmail,
    handleConcernClick,
    handleCloseAnnotationModal,
    handleNavigateBack,
  } = props;

  // Reset deactivation state when viewing a different user
  useEffect(() => {
    setIsUserDeactivated(false);
  }, [selectedProfile?.user_id, selectedProfile?._id]);

  useEffect(() => {
    setIsProfileImageLoaded(false);
  }, [selectedProfile?.input_image_url]);

  const handleOpenDeactivateDialog = () => {
    setDeactivateDialogOpen(true);
  };

  const handleCloseDeactivateDialog = () => {
    setDeactivateDialogOpen(false);
    setDeactivateReason('');
  };

  const handleConfirmDeactivate = async () => {
    if (!token || !distributorId) {
      console.error('Missing authentication credentials');
      return;
    }

    // Get the user_id from the selected profile, fallback to profileId if not available
    const targetUserId = selectedProfile?.user_id || '';

    if (!targetUserId) {
      console.error('No user ID found');
      setSnackbar({
        open: true,
        message: getTranslation("Txt978", "Unable to identify user to deactivate"),
        severity: 'error',
      });
      return;
    }

    setIsDeactivating(true);
    handleCloseDeactivateDialog();

    try {
      const apiService = new ApiService(sessionId, token);
      
      const response = await apiService.deactivateUser(targetUserId, deactivateReason);
      if (response.success) {
        setIsUserDeactivated(true);
        setSnackbar({
          open: true,
          message: getTranslation("Txt977", "User deactivated successfully"),
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: getTranslation("Txt976", "Failed to deactivate user"),
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : getTranslation("Txt976", "Failed to deactivate user"),
        severity: 'error',
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  // While details are loading, show only the heading and a loader
  if (isLoadingDetails) {
    return (
      <Layout showBackButton={true}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: '15px', md: '25px' },
            padding: { xs: '10px 0', md: '10px' },
            boxSizing: 'border-box',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '5px',
                flexGrow: 1,
              }}
            >
              <Typography
                sx={{
                  color: 'var(--color-text-primary)',
                  textAlign: 'center',
                  fontFamily: 'HerbalifeFont',
                  fontSize: { xs: '20px', md: '30px' },
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: 'normal',
                  paddingBottom: { xs: '5px', md: '10px' },
                }}
              >
                {getTranslation('Txt495')}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              minHeight: { xs: '200px', md: '300px' },
            }}
          >
            <CircularProgress sx={{ color: '#000' , fontSize: '28px' }} />
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: { xs: "15px", md: "25px" },
        padding: { xs: "10px 0", md: "10px" },
        boxSizing: "border-box",
        zIndex: 2,
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          zIndex: 2
        }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "5px",
              paddingTop: "",
              flexGrow: 1,
            }}>
            <Typography sx={{
              color: "var(--color-text-primary)",
              textAlign: "center",
              fontFamily: "HerbalifeFont",
              fontSize: { xs: "20px", md: "30px" },
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: 'normal',
              paddingBottom: { xs: "5px", md: "10px" }
            }}>
              {getTranslation("Txt495")}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "10px", md: '20px' },
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "center", md: "space-around" },
            padding: { xs: "10px", md: "20px" },
            boxSizing: "border-box",
            borderRadius: "10px",
            background: "#FFF",
            boxShadow: "2px 4px 24px 2px rgba(0, 0, 0, 0.10)",
            zIndex: 2,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              flexWrap: { xs: "wrap", md: "nowrap" },
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: "15px", md: "30px" },
              width: "100%",
              minWidth: 0,
              flex: { xs: "1 1 100%", md: "0 0 30%" },
              maxWidth: { xs: "100%", md: "30%" },
            }}>

            <Box
              id="modelImg"
              onClick={() => setFullImageModalOpen(true)}
              sx={{
                width: { xs: '120px', md: '100%' },
                maxWidth: { xs: '120px', md: '300px' },
                aspectRatio: '1 / 1',
                height: 'auto',
                flexShrink: 0,
                borderRadius: "50%",
                overflow: "hidden",
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              {!isProfileImageLoaded && (
                <Skeleton
                  variant="circular"
                  animation="wave"
                  sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: '#e0e0e0',
                  }}
                />
              )}
              <img
                src={selectedProfile?.input_image_url || "/default-profile-pic.svg"}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: isProfileImageLoaded ? 'block' : 'none',
                }}
                crossOrigin="anonymous"
                onLoad={() => setIsProfileImageLoaded(true)}
                onError={() => setIsProfileImageLoaded(true)}
              />
            </Box>
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <Box
                dir="ltr"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: { xs: "8px", md: "6px" },
                  marginBottom: { xs: "5px", md: "10px" },
                  width: "100%",
                  minWidth: 0,
                }}
              >
                {(role === 2
                  ? (userProfileData.first_name || userProfileData.last_name)
                  : selectedProfile?.name) && (
                    <PersonIcon sx={{ fontSize: { xs: "20px", md: "24px" }, color: "var(--color-text-secondary)", marginBottom: { xs: "2px", md: "6px" } }} />
                  )}
                <Typography
                  sx={{
                    color: "var(--color-text-secondary)",
                    textAlign: "center",
                    fontSize: { xs: "14px", md: "24px" },
                    fontStyle: "normal",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  {role === 2
                    ? `${(userProfileData.first_name || '').trim()} ${(userProfileData.last_name || '').trim()}`.trim()
                    : selectedProfile?.name || ''}
                </Typography>
              </Box>
              <Box
                dir="ltr"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: { xs: "8px", md: "6px" },
                  marginBottom: { xs: "5px", md: "10px" },
                  width: "100%",
                  minWidth: 0,
                }}
              >
                {(userProfileData.phone_number || selectedProfile?.cs_phone_number) && (
                  <PhoneIcon sx={{ fontSize: { xs: "20px", md: "24px" }, color: "var(--color-text-secondary)", marginBottom: { xs: "2px", md: "6px" } }} />
                )}
                <Typography
                  sx={{
                    color: "var(--color-text-secondary)",
                    textAlign: "center",
                    fontSize: { xs: "14px", md: "24px" },
                    fontStyle: "normal",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  {role === 2
                    ? (userProfileData.phone_number || '')
                    : (selectedProfile?.cs_phone_number || '')}
                </Typography>
              </Box>

              <Box
                dir="ltr"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: { xs: "8px", md: "6px" },
                  marginBottom: { xs: "5px", md: "10px" },
                  width: "100%",
                  minWidth: 0,
                }}
              >
                {(selectedProfile?.email || selectedProfile?.guest_email) && (
                  <EmailIcon sx={{ fontSize: { xs: "20px", md: "24px" }, color: "var(--color-text-secondary)", marginBottom: { xs: "2px", md: "3px" } }} />
                )}
                <Typography
                  sx={{
                    color: "var(--color-text-secondary)",
                    textAlign: "center",
                    fontSize: { xs: "14px", md: "24px" },
                    fontStyle: "normal",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  {selectedProfile?.email || selectedProfile?.guest_email || ''}
                </Typography>
                {selectedProfile?.email && (
                  <Tooltip title="Copy email">
                    <IconButton
                      onClick={handleCopyEmail}
                      size="small"
                      sx={{
                        padding: { xs: "4px", md: "6px" },
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)"
                        }
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: { xs: "16px", md: "20px" }, color: "var(--color-text-secondary)" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Typography
                sx={{
                  color: "var(--color-text-secondary)",
                  textAlign: "center",
                  fontSize: { xs: "14px", md: "24px" },
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: 'normal',
                  letterSpacing: "0.4px",
                }}
              >
                {getTranslation("Txt497").replace("-", " ")}{score}%
              </Typography>

              <Box
                sx={{
                  width: { xs: "200px", md: "252px" },
                  height: "10px",
                  borderRadius: "50px",
                  backgroundColor: "#D9D9D9",
                  overflow: "hidden",
                  margin: { xs: "auto", md: "auto" },
                  marginTop: { xs: "10px", md: "15px" },
                }}
              >
                <Box
                  sx={{
                    width: `${score}%`,
                    height: "100%",
                    backgroundColor: "#77DD77",
                    borderRadius: "50px",
                    float: isRTL ? "right" : "left",
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>

            </Box>
            {/* Legend */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'row', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: "15px", md: '15px' },
              marginTop: { xs: "10px", md: "15px" },
              flexWrap: 'wrap',
              width: '100%'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: "3px", md: '10px' },
                flexShrink: 0
              }}>
                <Box
                  sx={{
                    width: { xs: "12px", md: '25px' },
                    height: { xs: "12px", md: '25px' },
                    borderRadius: '50%',
                    backgroundColor: '#FF6961',
                    flexShrink: 0,
                    minWidth: { xs: "12px", md: '25px' },
                    minHeight: { xs: "12px", md: '25px' }
                  }}
                />
                <Typography sx={{
                  fontSize: { xs: "10px", md: '12px' },
                  fontWeight: 500,
                  whiteSpace: 'normal',
                  textAlign: 'center'
                }}>
                  {getTranslation("Txt498")}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: "3px", md: '10px' },
                flexShrink: 0
              }}>
                <Box
                  sx={{
                    width: { xs: "12px", md: '25px' },
                    height: { xs: "12px", md: '25px' },
                    borderRadius: '50%',
                    backgroundColor: '#FFB347',
                    flexShrink: 0,
                    minWidth: { xs: "12px", md: '25px' },
                    minHeight: { xs: "12px", md: '25px' }
                  }}
                />
                <Typography sx={{
                  fontSize: { xs: "10px", md: '12px' },
                  fontWeight: 500,
                  whiteSpace: 'normal',
                  textAlign: 'center'
                }}>
                  {getTranslation("Txt499")}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: "3px", md: '10px' },
                flexShrink: 0
              }}>
                <Box
                  sx={{
                    width: { xs: "12px", md: '25px' },
                    height: { xs: "12px", md: '25px' },
                    borderRadius: '50%',
                    backgroundColor: '#77DD77',
                    flexShrink: 0,
                    minWidth: { xs: "12px", md: '25px' },
                    minHeight: { xs: "12px", md: '25px' }
                  }}
                />
                <Typography sx={{
                  fontSize: { xs: "10px", md: '12px' },
                  fontWeight: 500,
                  whiteSpace: 'normal',
                  textAlign: 'center'
                }}>
                  {getTranslation("Txt500")}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              ...styles.inputContainer,
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: "16px", md: '20px' },
              order: { xs: 1, md: 0 },
              width: "100%",
              minWidth: 0,
              flex: { xs: "1 1 100%", md: "0 0 60%" },
              maxWidth: { xs: "100%", md: "60%" },
            }}>

            <Box sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              overflow: "visible",
              minWidth: 0,
            }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(3, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(auto-fit, minmax(140px, 1fr))',
                    lg: 'repeat(auto-fit, minmax(160px, 1fr))',
                    xl: 'repeat(4, 1fr)',
                  },
                  alignItems: 'start',
                  justifyContent: 'center',
                  gap: { xs: '10px', md: '30px' },
                  maxWidth: '100%',
                  width: '100%',
                  margin: '0 auto',
                  minWidth: 0,
                }}
              >
                {skinOutput.length !== 0 && skinOutput
                  .map((issue, index) => (
                    <Box
                      key={index}
                      onClick={() => !selectedConcernId && handleConcernClick(issue)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifySelf: 'center',
                        flexShrink: 0,
                        width: '100%',
                        maxWidth: '100%',
                        cursor: selectedConcernId ? 'default' : 'pointer',
                        opacity: selectedConcernId ? 0.6 : 1,
                        pointerEvents: selectedConcernId ? 'none' : 'auto',
                        '&:hover': {
                          transform: selectedConcernId ? 'none' : 'scale(0.95)',
                          transition: 'transform 0.2s ease-in-out',
                        },
                        padding: { xs: '5px', md: '0' },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: "50px", md: '100px' },
                          height: { xs: "50px", md: '100px' },
                          ...getBoxStyles(issue.riskLevel),
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'relative',
                        }}
                      >
                        <Typography
                          sx={{
                            color: 'var(--color-text-inverse)',
                            textAlign: 'center',
                            fontSize: { xs: "16px", md: '32px' },
                            fontWeight: 700,
                            lineHeight: { xs: '16px', md: '32px' },
                            position: 'absolute',
                          }}
                        >
                          {issue.score}%
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          color: 'var(--color-text-primary)',
                          textAlign: 'center',
                          fontFamily: 'HerbalifeFont',
                          fontSize: { xs: "10px", md: '18px' },
                          fontWeight: 400,
                          lineHeight: { xs: 1.35, md: 1.45 },
                          letterSpacing: '0.01em',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          marginTop: { xs: "8px", md: '20px' },
                          borderBottom: '1px solid var(--color-border-dark)',
                        }}
                      >
                        {getConcernTranslation(getTranslation, ConcernDescriptions.find((cd) => cd.id === issue.concern)?.key!)}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>

            {/* <Box sx={{ display: 'flex', justifyContent: "center", alignItems: "center", width: "100%" }}>
            <Button
              variant="contained"
              sx={{
                width: '143px',
                height: '40.857px',
                flexShrink: 0,
                borderRadius: '5px',
                background: 'var(--color-primary)',
                fontSize: '16px',
                color: 'var(--color-text-inverse)',
                '&:hover': {
                  background: '#333',
                },
              }}
              onClick={() => setView("Details")}
            >
              Next
            </Button>
          </Box> */}
          </Box>

          <Modal
            open={!!selectedConcernId}
            onClose={handleCloseAnnotationModal}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 300,
                sx: {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                },
              },
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                minWidth: { xs: '200px', md: '280px' },
                minHeight: { xs: '200px', md: '280px' },
              }}
            >
              {!isImageLoaded || !selectedAnnotationImage ? (
                <CircularProgress sx={{ color: 'white' }} />
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    maxWidth: { xs: '90vw', md: '90vw' },
                    maxHeight: { xs: '90vh', md: '90vh' },
                    borderRadius: '10px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <Box
                    component="img"
                    src={selectedAnnotationImage}
                    alt="Annotation"
                    sx={{
                      maxWidth: { xs: '90vw', md: '90vw' },
                      maxHeight: { xs: '90vh', md: '90vh' },
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                  <Typography
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      width: '100%',
                      color: 'var(--color-text-primary)',
                      fontSize: { xs: '12px', md: '14px' },
                      fontWeight: 400,
                      backgroundColor: 'rgba(239, 239, 239, 0.8)',
                      padding: '5px',
                      lineHeight: 'normal',
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      wordWrap: 'break-word',
                    }}
                  >
                    {getConcernTranslation(getTranslation, ConcernDescriptions.find((cd) => cd.id === selectedConcernId)?.key!)} - {getTranslation(ConcernDescriptions.find((cd) => cd.id === selectedConcernId)?.text!)}
                  </Typography>
                </Box>
              )}
              <IconButton
                onClick={handleCloseAnnotationModal}
                sx={{
                  position: 'absolute',
                  top: { xs: '5px', md: '10px' },
                  right: { xs: '5px', md: '10px' },
                  backgroundColor: 'var(--color-background)',
                  borderRadius: '50%',
                  color: 'var(--color-text-primary)',
                  zIndex: 1,
                  padding: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: { xs: '24px', md: '28px' } }} />
              </IconButton>
            </Box>
          </Modal>

          {/* Full Image Modal */}
          <Modal
            open={fullImageModalOpen}
            onClose={() => setFullImageModalOpen(false)}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
                sx: {
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
              },
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <Fade in={fullImageModalOpen} timeout={500}>
              <Box
                sx={{
                  position: 'relative',
                  maxWidth: { xs: '95%', md: '90%' },
                  maxHeight: { xs: '95%', md: '90%' },
                  outline: 'none',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                  boxSizing: 'border-box',
                  transform: 'scale(0.9)',
                  opacity: 0,
                  transition: 'transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '&.MuiFade-entered': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                }}
              >
                <img
                  src={selectedProfile?.input_image_url || "/default-profile-pic.svg"}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    maxHeight: '90vh',
                  }}
                  crossOrigin="anonymous"
                />
                <IconButton
                  onClick={() => setFullImageModalOpen(false)}
                  sx={{
                    position: 'absolute',
                    top: { xs: '5px', md: '10px' },
                    right: { xs: '5px', md: '10px' },
                    backgroundColor: 'var(--color-background)',
                    borderRadius: '50%',
                    color: 'var(--color-text-primary)',
                    zIndex: 1001,
                    padding: '0',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                  aria-label="Close"
                >
                  <CloseIcon sx={{ fontSize: { xs: '24px', md: '28px' } }} />
                </IconButton>
              </Box>
            </Fade>
          </Modal>
        </Box>

        {moreProfileData.length > 0 && role === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "10px", md: '60px' },
              alignItems: { xs: "center", md: "center" },
              justifyContent: { xs: "center", md: "flex-start" },
              padding: { xs: "10px", md: "40px" },
              boxSizing: "border-box",
              borderRadius: "10px",
              background: "#FFF",
              boxShadow: "2px 4px 24px 2px rgba(0, 0, 0, 0.10)",
              width: "100%",
              zIndex: 2
            }}
          >
            <Recommendation profileData={moreProfileData} clickedUserId={profileId} />
          </Box>
        )}
        {SHOW_SKIN_CHARTS && role === 1 && selectedProfile && selectedProfile.is_anonymous === false && filteredProfileData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "10px", md: '60px' },
              alignItems: { xs: "center", md: "center" },
              justifyContent: { xs: "center", md: "space-around" },
              padding: { xs: "0px", md: "40px" },
              boxSizing: "border-box",
              borderRadius: "10px",
              background: "#FFF",
              boxShadow: "2px 4px 24px 2px rgba(0, 0, 0, 0.10)",
              width: "100%",
              zIndex: 2
            }}
          >
            <SingleChart profileData={filteredProfileData} clickedUserId={profileId} />
          </Box>
        )}
        {SHOW_SKIN_CHARTS && role === 1 && selectedProfile && selectedProfile.is_anonymous === false && filteredProfileData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "25px", md: '60px' },
              alignItems: { xs: "center", md: "center" },
              justifyContent: { xs: "center", md: "space-around" },
              padding: { xs: "0px", md: "40px" },
              borderRadius: "10px",
              background: "#FFF",
              boxShadow: "2px 4px 24px 2px rgba(0, 0, 0, 0.10)",
              width: "100%",
              zIndex: 2
            }}
          >
            <MultiLineChart profileData={filteredProfileData} clickedUserId={profileId} />
          </Box>
        )}
        {SHOW_SKIN_CHARTS && role === 1 && selectedProfile && selectedProfile.is_anonymous === false && filteredProfileData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "25px", md: '60px' },
              alignItems: { xs: "center", md: "center" },
              justifyContent: { xs: "center", md: "space-around" },
              padding: { xs: "0px", md: "40px" },
              borderRadius: "10px",
              background: "#FFF",
              boxShadow: "2px 4px 24px 2px rgba(0, 0, 0, 0.10)",
              width: "100%",
              zIndex: 2
            }}
          >
            <SkinHealthMetrics profileData={filteredProfileData} clickedUserId={profileId} />
          </Box>
        )}
        {SHOW_SKIN_CHARTS && filteredProfileData.length > 0 && role === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "25px", md: '60px' },
              alignItems: { xs: "center", md: "center" },
              justifyContent: { xs: "center", md: "space-around" },
              padding: { xs: "15px", md: "40px" },
              borderRadius: "10px",
              background: "#FFF",
              boxShadow: "2px 4px 24px 2px rgba(0, 0, 0, 0.10)",
              width: "100%",
              zIndex: 2
            }}
          >
            <AnalysisHistory clickedUserId={profileId} />
          </Box>
        )}
      </Box>
      {role === 2 && (

        <Box sx={{
          width: '100%',
          mt: { xs: "20px", md: "30px" },
          maxWidth: { 
              xs: '400px', 
              sm: '500px',
              md: '450px',     // 1280x720 - compact distributor box
              lg: '550px',     // 1366x657 - moderate distributor box
              xl: '680px'      // 1920x950 - generous distributor box
          },
          padding: { 
              xs: '16px 12px', 
              md: '15px 18px',     // 1280x720 - minimal padding
              lg: '20px 25px',     // 1366x657 - moderate padding
              xl: '25px 35px'      // 1920x950 - generous padding
          },
          borderRadius: {
            xs: '12px',
            md: '14px',
            lg: '18px',
            xl: '22px'
          },
          backgroundColor: '#ffffff',
          boxShadow: {
            xs: '0 2px 8px rgba(0, 0, 0, 0.1)',
              md: '0 3px 12px rgba(0, 0, 0, 0.08)', 
              lg: '0 4px 16px rgba(0, 0, 0, 0.1)', 
              xl: '0 6px 24px rgba(0, 0, 0, 0.12)' 
          },
          border: { md: '1px solid var(--color-border-light)' }
        }}>
          {userProfilesCache.data && (() => {
              const { distributorName } = getDistributorInfoAndName(userProfilesCache.data);
              
              return (
                  <Typography sx={{
                      color: 'var(--color-text-primary)',
                      textAlign: 'center',
                      fontSize: { 
                          xs: '12px', 
                          md: '13px',      // 1280x720 - compact text
                          lg: '15px',      // 1366x657 - moderate text
                          xl: '17px'       // 1920x950 - comfortable text
                      },
                      fontWeight: { xs: 400, md: 500 },
                      lineHeight: { xs: '1.4', md: '1.5' },
                      marginBottom: { 
                          xs: '12px', 
                          md: '10px', 
                          lg: '12px', 
                          xl: '16px' 
                      },
                      letterSpacing: { 
                          md: '0.2px', 
                          lg: '0.3px', 
                          xl: '0.4px' 
                      },
                      fontFamily: 'HerbalifeFont'
                  }}>
                      {getTranslation("Txt75").replace(/\[.*?\]/g, distributorName)}
                  </Typography>
              );
          })()}
          

        {userProfilesCache.data && (() => {
          const { distributorInfo } = getDistributorInfoAndName(userProfilesCache.data);
          return distributorInfo;
        })() && (

              <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: '4px', md: '20px' },
                  fontSize: { 
                      xs: '12px', 
                      md: '12px',      // 1280x720 - compact email text
                      lg: '14px',      // 1366x657 - moderate email text
                      xl: '16px'       // 1920x950 - comfortable email text
                  },
                  color: 'var(--color-text-secondary)',
                  padding: { 
                      md: '8px 0', 
                      lg: '10px 0', 
                      xl: '12px 0' 
                  }
              }}>
                                              {(() => {
                      const { distributorInfo } = getDistributorInfoAndName(userProfilesCache.data);

                      return distributorInfo ? (
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Typography sx={{
                              fontSize: 'inherit',
                              color: 'inherit',
                              fontFamily: 'HerbalifeFont',
                              textAlign: 'center',
                              fontWeight: { md: 500 },
                              letterSpacing: { 
                                  md: '0.2px', 
                                  lg: '0.3px', 
                                  xl: '0.4px' 
                              }
                          }}>
                                <strong>{getTranslation("Txt76", "Email : [Distributor's Email]").split(':')[0] + ':'}</strong> {distributorInfo.primary_email}
                          </Typography>
                          <Typography sx={{
                              fontSize: 'inherit',
                              color: 'inherit',
                              fontFamily: 'HerbalifeFont',
                              textAlign: 'center',
                              fontWeight: { md: 500 },
                              letterSpacing: { 
                                  md: '0.2px', 
                                  lg: '0.3px', 
                                  xl: '0.4px' 
                              }
                          }}>
                                <strong>{getTranslation("Txt77", "Phone Number : [Distributor's Phone Number]").split(':')[0] + ':'}</strong> {distributorInfo.phone_number}
                          </Typography>
                        </Box>
                      ) : null;
                  })()}
              </Box>
          )}
        </Box>

      )}
      <Box className="center" sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1.5, md: 2 },
        mt: { xs: 2, md: 3 },
        mb: { xs: 0, md: 2 },
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}>
        <Button
          variant="contained"
          sx={{
            ...COMMON_MUI_STYLES.primaryCtaButton2,
            width: { xs: "100%", sm: "60%", md: "auto" },
          }}
          onClick={handleNavigateBack}
        >
          {getTranslation("Txt743")}
        </Button>
        {role === 1 && distributorTableView === 'customer' && (
          <Button
            variant="contained"
            sx={{
              ...COMMON_MUI_STYLES.primaryCtaButton2,
              width: { xs: "100%", sm: "60%", md: "auto" },
              textAlign: 'center',
              backgroundColor: isUserDeactivated ? '#9e9e9e' : '#000000',
              '&:hover': {
                backgroundColor: isUserDeactivated ? '#9e9e9e' : '#333333',
              },
              '&:disabled': {
                backgroundColor: '#9e9e9e',
                color: 'var(--color-text-inverse)',
              },
            }}
            disabled={isDeactivating || isUserDeactivated}
            onClick={handleOpenDeactivateDialog}
          >
            {isDeactivating ? getTranslation("Txt980", "Deactivating...") : isUserDeactivated ? getTranslation("Txt970", "Deactivated") : getTranslation("Txt979", "Deactivate User")}
          </Button>
        )}
      </Box>
      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={handleCloseDeactivateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: { xs: '20px', md: '30px' },
          }
        }}
      >
        <IconButton
          onClick={handleCloseDeactivateDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'var(--color-text-primary)',
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ padding: 0, marginTop: '20px', overflowY: 'unset' }}>
          <Typography
            sx={{
              fontSize: { xs: '16px', md: '18px' },
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              textAlign: 'center',
              marginBottom: '16px',
              fontFamily: 'HerbalifeFont',
            }}
          >
            {getTranslation("Txt975", "Are you sure you want to deactivate this user's profile?")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '13px', md: '14px' },
              fontWeight: 400,
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              marginBottom: '24px',
              fontFamily: 'HerbalifeFont',
            }}
          >
            {getTranslation("Txt974", "Please enter a reason for deactivating this user's profile.")}
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder={getTranslation("Txt971", "Add your reason (optional)")}
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            sx={{
              marginBottom: '24px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              sx={COMMON_MUI_STYLES.primaryCtaButton2}
              onClick={handleConfirmDeactivate}
            >
              {getTranslation("Txt972", "Yes")}
            </Button>
            <Button
              variant="contained"
              sx={COMMON_MUI_STYLES.primaryCtaButton2}
              onClick={handleCloseDeactivateDialog}
            >
              {getTranslation("Txt973", "No")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

const MoreUserDetailsRouter = () => {
  const { role, distributorTableView } = useAppContext();

  if (role === 2) return <CustomerDataTable />;
  if (role === 1 && distributorTableView === 'customer') return <DistributorCustomerDataTable />;
  if (role === 1 && distributorTableView === 'distributor') return <DistributorDistributorDataTable />;

  // Fallback
  return <CustomerDataTable />;
};

export default MoreUserDetailsRouter;

